import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useSnackbar } from '../context/SnackbarContext';
import StripePayment from '../components/StripePayment';
import './Wallet.css';

interface Transaction {
  id: string;
  type: 'FEE' | 'TOPUP' | 'PURCHASE' | 'REFUND' | 'CASHBACK';
  amount: number;
  description: string;
  paymentMethod?: string;
  status?: 'pending' | 'completed' | 'rejected';
  createdAt: string;
}

interface WalletData {
  balance: number;
  transactions: Transaction[];
}

interface MembershipPlan {
  id: string;
  name: string;
  minimumDeposit: number;
  percentageDiscount: number;
  cashbackEnabled: boolean;
  cashbackPercentage: number;
  cashbackTiming: 'on_topup' | 'on_spend';
  priceMatching: boolean;
  pointsConversionRate: number;
  freeDelivery: boolean;
  status?: 'active' | 'inactive' | 'closed' | 'teaser';
}

const Wallet: React.FC = () => {
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [currentPlan, setCurrentPlan] = useState<MembershipPlan | null>(null);
  const [paymentType, setPaymentType] = useState<'card' | 'etransfer'>('card');
  const [interacSubmitting, setInteracSubmitting] = useState(false);
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [showPlanComparison, setShowPlanComparison] = useState(false);
  const { showError, showSuccess } = useSnackbar();
  const interacEmail = useMemo(
    () => process.env.REACT_APP_INTERAC_EMAIL || 'payments@clubkazi.com',
    []
  );
  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => a.minimumDeposit - b.minimumDeposit),
    [plans]
  );
  const handleClosePlanDetails = () => {
    setShowPlanDetails(false);
    setShowPlanComparison(false);
  };
  const amountValue = useMemo(() => parseFloat(topUpAmount), [topUpAmount]);
  const amountError = useMemo(() => {
    if (!topUpAmount) return 'Select or enter an amount.';
    if (Number.isNaN(amountValue)) return 'Enter a valid amount.';
    if (amountValue < 500) return 'Amount must be at least $500.';
    if (amountValue % 500 !== 0) return 'Amount must be in multiples of $500.';
    return '';
  }, [amountValue, topUpAmount]);

  useEffect(() => {
    fetchWallet();
    fetchPlans();
    fetchCurrentPlan();
  }, []);

  const fetchWallet = async () => {
    try {
      const response = await api.get('/wallet/balance');
      setWallet(response.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/membership-plans');
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching membership plans:', error);
    }
  };

  const fetchCurrentPlan = async () => {
    try {
      const response = await api.get('/users/me/plan');
      setCurrentPlan(response.data?.plan || null);
    } catch (error) {
      setCurrentPlan(null);
    }
  };

  const handleTopUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(topUpAmount);
    if (isNaN(amount) || amount <= 0) {
      showError('Please enter a valid amount');
      return;
    }
    if (amount < 500) {
      showError('Top-up amount must be at least $500');
      return;
    }

    // Validate amount is multiple of $500
    if (amount % 500 !== 0) {
      showError('Top-up amount must be in multiples of $500');
      return;
    }

    if (paymentType === 'card') {
      setShowPaymentForm(true);
      return;
    }

    setInteracSubmitting(true);
    try {
      await api.post('/wallet/topup/etransfer', { amount });
      showSuccess('Interac transfer noted. Pending admin approval.');
      setTopUpAmount('');
      await fetchWallet();
    } catch (error: any) {
      showError(error.response?.data?.error || 'Unable to mark transfer as sent.');
    } finally {
      setInteracSubmitting(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    setTopUpAmount('');
    fetchWallet();
  };

  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
  };

  const getTransactionTypeLabel = (type: Transaction['type']) => {
    switch (type) {
      case 'FEE':
        return 'Membership Fee';
      case 'TOPUP':
        return 'Top-up';
      case 'PURCHASE':
        return 'Purchase';
      case 'REFUND':
        return 'Refund';
      case 'CASHBACK':
        return 'Cashback';
      default:
        return type;
    }
  };

  const getTransactionColor = (type: Transaction['type']) => {
    switch (type) {
      case 'FEE':
      case 'TOPUP':
      case 'REFUND':
      case 'CASHBACK':
        return '#059669';
      case 'PURCHASE':
        return '#dc2626';
      default:
        return '#1a1a1a';
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="wallet-skeleton">
          <div className="wallet-skeleton-card">
            <div className="wallet-skeleton-line title" />
            <div className="wallet-skeleton-line amount" />
          </div>
          <div className="wallet-skeleton-card">
            <div className="wallet-skeleton-line title" />
            <div className="wallet-skeleton-line meta" />
            <div className="wallet-skeleton-line meta short" />
          </div>
          <div className="wallet-skeleton-card">
            <div className="wallet-skeleton-line title" />
            <div className="wallet-skeleton-line meta" />
            <div className="wallet-skeleton-line meta" />
          </div>
        </div>
      </div>
    );
  }

  const canTopUp = currentPlan && currentPlan.status === 'active';

  return (
    <div className="container">
      <div className="page-header">
        <h1>My Wallet</h1>
        <p>Manage your balance and view transactions</p>
      </div>

      {!canTopUp && (
        <div className="wallet-plan-required-banner" role="alert">
          <p>You need an active membership plan to top up your wallet.</p>
          <Link to="/plans" className="btn btn-primary">
            Choose a plan
          </Link>
        </div>
      )}
      
      <div className="wallet-balance-card">
        <h2>Current Balance</h2>
        <div className="balance-amount">${wallet?.balance.toFixed(2) || '0.00'}</div>
      </div>

      <div className="card wallet-plan-card">
        <div className="wallet-plan-header">
          <div>
            <h2>Current Plan</h2>
            <p className="wallet-plan-name">{currentPlan?.name || 'No plan assigned'}</p>
          </div>
          <button
            type="button"
            className="plan-info-icon"
            aria-label="View current plan benefits"
            onClick={() => setShowPlanDetails(true)}
          >
            i
          </button>
        </div>
        <div className="wallet-plan-actions">
          <Link to="/plans" className="btn btn-secondary">
            Compare other plans
          </Link>
          <div className="wallet-plan-note">
            Want to migrate plans? Reach out to admin support.
          </div>
        </div>
      </div>
      {showPlanDetails && (
        <div className="plan-modal-overlay" role="dialog" aria-modal="true">
          <div className="plan-modal">
            <div className="plan-modal-header">
              <h3>{currentPlan?.name || 'Plan benefits'}</h3>
              <button
                type="button"
                className="plan-modal-close"
                onClick={handleClosePlanDetails}
                aria-label="Close plan details"
              >
                ×
              </button>
            </div>
            <ul className="plan-modal-list">
              <li>Discount: {currentPlan?.percentageDiscount ?? 0}%</li>
              <li>
                Cashback:{' '}
                {currentPlan?.cashbackEnabled
                  ? `${currentPlan.cashbackPercentage}% (${currentPlan.cashbackTiming === 'on_spend' ? 'on spend' : 'on top-up'})`
                  : 'No cashback'}
              </li>
              <li>{currentPlan?.freeDelivery ? 'Free delivery' : 'Delivery fees apply'}</li>
              <li>{currentPlan?.priceMatching ? 'Price matching' : 'No price matching'}</li>
              <li>Points conversion: {currentPlan?.pointsConversionRate ?? 0}</li>
            </ul>
            {sortedPlans.length > 0 && showPlanComparison && (
              <div className="plan-compare-table">
                <div className="plan-compare-row header">
                  <div>Benefit</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>{plan.name}</div>
                  ))}
                </div>
                <div className="plan-compare-row">
                  <div>Minimum deposit</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>${plan.minimumDeposit.toFixed(0)}</div>
                  ))}
                </div>
                <div className="plan-compare-row">
                  <div>Member discount</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>{plan.percentageDiscount}%</div>
                  ))}
                </div>
                <div className="plan-compare-row">
                  <div>Cashback</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>
                      {plan.cashbackEnabled
                        ? `${plan.cashbackPercentage}% (${plan.cashbackTiming === 'on_spend' ? 'spend' : 'top-up'})`
                        : 'No'}
                    </div>
                  ))}
                </div>
                <div className="plan-compare-row">
                  <div>Free delivery</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>{plan.freeDelivery ? 'Yes' : 'No'}</div>
                  ))}
                </div>
                <div className="plan-compare-row">
                  <div>Price matching</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>{plan.priceMatching ? 'Yes' : 'No'}</div>
                  ))}
                </div>
                <div className="plan-compare-row">
                  <div>Points conversion</div>
                  {sortedPlans.map(plan => (
                    <div key={plan.id}>{plan.pointsConversionRate}</div>
                  ))}
                </div>
              </div>
            )}
            <div className="plan-modal-actions">
              <Link to="/plans" className="btn btn-secondary" onClick={handleClosePlanDetails}>
                Compare plans
              </Link>
              {sortedPlans.length > 0 && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPlanComparison(prev => !prev)}
                >
                  {showPlanComparison ? 'Hide comparison' : 'Compare here'}
                </button>
              )}
              <button type="button" className="btn btn-primary" onClick={handleClosePlanDetails}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {canTopUp && (
      <div className="card">
        <h2>Top Up Wallet</h2>
        <p className="topup-note">Top-up amounts must be in multiples of $500</p>
        <div className="topup-highlight">All top-ups are available for shopping only.</div>
        <div className="topup-payment-toggle">
          <button
            type="button"
            className={`topup-toggle-btn ${paymentType === 'card' ? 'active' : ''}`}
            aria-pressed={paymentType === 'card'}
            onClick={() => {
              setPaymentType('card');
              setShowPaymentForm(false);
            }}
          >
            Card payment
          </button>
          <button
            type="button"
            className={`topup-toggle-btn ${paymentType === 'etransfer' ? 'active' : ''}`}
            aria-pressed={paymentType === 'etransfer'}
            onClick={() => {
              setPaymentType('etransfer');
              setShowPaymentForm(false);
            }}
          >
            Interac e-Transfer
          </button>
        </div>
        {paymentType === 'card' && (
          <div className="topup-fee-note">
            Card payments include processing fees paid by you. Estimated fee: 2.9% + $0.30.
          </div>
        )}
        {!showPaymentForm ? (
          <form onSubmit={handleTopUp} className="topup-form">
            <div className="form-group">
              <label>Select amount</label>
              <div className="quick-amounts">
                {[500, 1000, 2000].map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    className={`quick-amount-btn ${
                      !useCustomAmount && topUpAmount === String(amount) ? 'active' : ''
                    }`}
                    onClick={() => {
                      setUseCustomAmount(false);
                      setTopUpAmount(String(amount));
                    }}
                  >
                    ${amount.toLocaleString()}
                  </button>
                ))}
                <button
                  type="button"
                  className={`quick-amount-btn ${useCustomAmount ? 'active' : ''}`}
                  onClick={() => {
                    setUseCustomAmount(true);
                    setTopUpAmount('');
                  }}
                >
                  Other
                </button>
              </div>
              {useCustomAmount && (
                <div className="custom-amount">
                  <label>Other amount ($)</label>
                  <input
                    type="number"
                    step="500"
                    min="500"
                    value={topUpAmount}
                    onChange={(e) => setTopUpAmount(e.target.value)}
                    placeholder="500, 1000, 1500, etc."
                    required
                  />
                </div>
              )}
            </div>
            {amountError && (
              <div className="amount-error" role="alert">
                {amountError}
              </div>
            )}
            {paymentType === 'card' ? (
              <button type="submit" className="btn btn-primary" disabled={showPaymentForm || Boolean(amountError)}>
                Continue to Payment
              </button>
            ) : (
              <div className="interac-instructions">
                <div className="interac-email">
                  Send Interac transfer to: <strong>{interacEmail}</strong>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(interacEmail);
                    showSuccess('Interac email copied');
                  }}
                >
                  Copy email
                </button>
                <button type="submit" className="btn btn-primary" disabled={interacSubmitting || Boolean(amountError)}>
                  {interacSubmitting ? 'Submitting...' : 'I have sent the transfer'}
                </button>
              </div>
            )}
          </form>
        ) : (
          <div className="payment-container">
            <StripePayment
              amount={parseFloat(topUpAmount)}
              onSuccess={handlePaymentSuccess}
              onCancel={handlePaymentCancel}
            />
          </div>
        )}
      </div>
      )}

      <div className="card">
        <h2>Transaction History</h2>
        {wallet && wallet.transactions.length === 0 ? (
          <div className="transactions-empty">
            <div className="transactions-empty-icon">🧾</div>
            <h3>No transactions yet</h3>
            <p>Your top-ups and purchases will appear here.</p>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Top up wallet
            </button>
          </div>
        ) : (
          <div className="transactions-list">
            {wallet?.transactions.map((transaction) => (
              <div key={transaction.id} className="transaction-item">
                <div className="transaction-info">
                  <div className="transaction-type">
                    {getTransactionTypeLabel(transaction.type)}
                  </div>
                  <div className="transaction-description">
                    {transaction.description}
                    {transaction.paymentMethod && (
                      <span className="transaction-method">{transaction.paymentMethod}</span>
                    )}
                    {transaction.status === 'pending' && (
                      <span className="transaction-status pending">Pending approval</span>
                    )}
                  </div>
                  <div className="transaction-date">
                    {new Date(transaction.createdAt).toLocaleString()}
                  </div>
                </div>
                <div
                  className="transaction-amount"
                  style={{ color: getTransactionColor(transaction.type) }}
                >
                  {transaction.type === 'PURCHASE' ? '-' : '+'}$
                  {transaction.amount.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wallet;

